import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../../../environments/environment';

// Interfacce per le API di Zammad
// Struttura conforme al DTO Java del backend
export interface ZammadAttachment {
  filename: string;
  data: string; // Base64 senza prefisso data:
  mimeType: string;
}

export interface ZammadTicketRequest {
  nome: string;
  email: string;
  subject: string;
  message: string;
  attachments?: ZammadAttachment[];
}

export interface ZammadTicketResponse {
  id: number;
  number: string;
  title: string;
  state_id: number;
  priority_id: number;
  group_id: number;
  customer_id: number;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class ZammadService {
  // Chiama il backend AgriFlow che fa da proxy per Zammad
  // Il backend gestisce il token API Zammad in modo sicuro
  private readonly apiUrl = environment.apiUrl + 'support-ticket';

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
  ) {
    this.logger.info('[ZAMMAD SERVICE] Configurato con apiUrl:', this.apiUrl);
  }

  getTicket(ticketId: string): Observable<ZammadTicketResponse> {
    const token = localStorage.getItem('auth_token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.get<ZammadTicketResponse>(`${this.apiUrl}/${ticketId}`, {
      headers,
    });
  }

  getTickets(): Observable<ZammadTicketResponse[]> {
    const token = localStorage.getItem('auth_token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.get<ZammadTicketResponse[]>(this.apiUrl, {
      headers,
    });
  }
  /**
   * Crea un nuovo ticket su Zammad
   */
  createTicket(
    customerName: string,
    customerEmail: string,
    subject: string,
    message: string,
    attachments?: File[],
  ): Observable<ZammadTicketResponse> {
    this.logger.info('[ZAMMAD SERVICE] Creazione ticket in corso...', {
      nome: customerName,
      email: customerEmail,
      subject,
    });

    // Costruisci il payload del ticket conforme al DTO Java
    const ticketPayload: ZammadTicketRequest = {
      nome: customerName,
      email: customerEmail,
      subject: subject,
      message: message,
    };

    // Se ci sono allegati, convertili in base64
    if (attachments && attachments.length > 0) {
      return this.convertFilesToAttachments(attachments).pipe(
        map((zammadAttachments) => {
          ticketPayload.attachments = zammadAttachments;
          return ticketPayload;
        }),
        switchMap((payload) => this.sendTicketRequest(payload)),
        catchError((error) => {
          this.logger.error(
            '[ZAMMAD SERVICE] Errore durante conversione allegati:',
            error,
          );
          return throwError(() => error);
        }),
      );
    }

    // Invia la richiesta senza allegati
    return this.sendTicketRequest(ticketPayload);
  }

  /**
   * Invia la richiesta di creazione ticket tramite il backend AgriFlow
   * Il backend si occuperà di chiamare Zammad con il token sicuro
   */
  private sendTicketRequest(
    payload: ZammadTicketRequest,
  ): Observable<ZammadTicketResponse> {
    const token = localStorage.getItem('auth_token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    this.logger.info(
      '[ZAMMAD SERVICE] Invio richiesta al backend AgriFlow...',
      payload,
    );

    return this.http
      .post<ZammadTicketResponse>(this.apiUrl, payload, {
        headers,
      })
      .pipe(
        map((response) => {
          this.logger.info(
            '[ZAMMAD SERVICE] Ticket creato con successo:',
            response,
          );
          return response;
        }),
        catchError((error) => {
          this.logger.error(
            '[ZAMMAD SERVICE] Errore durante creazione ticket:',
            error,
          );
          return throwError(() => error);
        }),
      );
  }

  /**
   * Converte i file in oggetti ZammadAttachment con filename, data e mimeType
   */
  private convertFilesToAttachments(
    files: File[],
  ): Observable<ZammadAttachment[]> {
    return new Observable((observer) => {
      const attachments: ZammadAttachment[] = [];
      let filesProcessed = 0;

      files.forEach((file) => {
        const reader = new FileReader();

        reader.onload = () => {
          // Ottieni il data URL completo (es: "data:image/png;base64,iVBORw0KGgo...")
          const dataUrl = reader.result as string;

          // Estrai solo la parte base64 (rimuove il prefisso "data:...;base64,")
          const base64Data = dataUrl.split(',')[1];

          // Crea l'oggetto allegato con il formato richiesto da Zammad
          const attachment: ZammadAttachment = {
            filename: file.name,
            data: base64Data,
            mimeType: file.type || 'application/octet-stream',
          };

          attachments.push(attachment);
          filesProcessed++;

          if (filesProcessed === files.length) {
            this.logger.info(
              `[ZAMMAD SERVICE] ${attachments.length} allegati convertiti:`,
              attachments.map((a) => `${a.filename} (${a.mimeType})`),
            );
            observer.next(attachments);
            observer.complete();
          }
        };

        reader.onerror = (error) => {
          this.logger.error(
            '[ZAMMAD SERVICE] Errore durante lettura file:',
            error,
          );
          observer.error(error);
        };

        reader.readAsDataURL(file);
      });
    });
  }

  /**
   * Ottiene la lista dei gruppi disponibili (opzionale, per configurazione futura)
   */
  getGroups(): Observable<any[]> {
    const token = localStorage.getItem('auth_token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.get<any[]>(`${this.apiUrl}/groups`, { headers }).pipe(
      map((groups) => {
        this.logger.info('[ZAMMAD SERVICE] Gruppi disponibili:', groups);
        return groups;
      }),
      catchError((error) => {
        this.logger.error(
          '[ZAMMAD SERVICE] Errore durante recupero gruppi:',
          error,
        );
        return throwError(() => error);
      }),
    );
  }
}
